import { create } from "zustand";
import { UserUMS } from "../types/Users";
import { getUserByQId, getUserByUsername } from "../utils/api/Users";
import { validate } from "uuid";

interface ReportingUserStore {
  users: Record<string, UserUMS>;
  getUser: (userID: string) => Promise<UserUMS>;
  reset: () => void;
}

/**
 * Store for sharing user details between Reporting pages to avoid constant
 * re-fetching as customer navigates back-and-forth.
 */
export const useReportingUserStore = create<ReportingUserStore>((set, get) => ({
  users: {},

  getUser: async (userID): Promise<UserUMS> => {
    let user = get().users[userID];
    if (user) {
      return user;
    }

    let error;
    if (validate(userID)) {
      try {
        user = await getUserByQId(userID);
        user.id = user.qoriaUserID ?? userID;
      } catch (err) {
        error = err;
      }
    }

    if (!user && !error) {
      try {
        // fallback for backwards compatibility
        user = await getUserByUsername(userID);
        if (user.qoriaUserID) {
          user.id = user.qoriaUserID;
        }
      } catch (err) {
        error = err;

        if (!!err && typeof err === "object" && "status" in err && err.status === 404) {
          // HIT-5369: Reporting includes users without corresponding UMS record whose
          // data must be viewable by admins hence we create a virtual user here.
          // When a device is connected to appliance network without any other agent
          // software on the device, a 'user' in Reporting will be either:
          // - a Guest username (CMS has a full name, but no API to retrieve it...), or
          // - a plain IP or MAC address
          user = {
            id: btoa(userID),
            username: userID,
            firstName: "",
            lastName: "",
          } as UserUMS;
        }
      }
    }

    if (!user) {
      throw error;
    }

    set({
      users: {
        ...get().users,
        [user.id]: user,
        [user.username]: user,
      },
    });

    return user;
  },

  reset: () => {
    set({ users: {} });
  },
}));
